/**
 * PLBenchmarkComparison - Compare P&L metrics against industry benchmarks
 * 
 * Displays a visual comparison of the business's expense ratios and margins
 * against industry norms. Shows deviation levels, health score, and forensic implications.
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  RefreshCw,
  Target,
  Building2,
  PieChart
} from 'lucide-react';
// 2026-04-29 (TS-FU3 batch 3): same latent runtime bug as
// AdjusterQABot — was importing `api` (default) from services/api,
// whose default export has only domain helpers (getOrganization etc),
// not the generic `get/post/delete` methods used below. The cached/
// compare benchmark fetches were silently no-oping inside the surrounding
// try/catch. Switched to utils/api which has the generic methods.
// apiFetch returns the JSON body directly (no `.data` wrapper).
import { api } from '../utils/api';
import { FinancialValue } from './shared/FinancialValue';

interface MetricComparison {
  metric_key: string;
  metric_name: string;
  description: string;
  business_value: number;
  benchmark_low: number;
  benchmark_mid: number;
  benchmark_high: number;
  unit: string;
  deviation_percent: number;
  deviation_level: string;
  percentile: number;
  explanation: string;
  claim_impact: string;
}

interface BenchmarkSummary {
  total_metrics: number;
  favorable_count: number;
  normal_count: number;
  high_deviation_count: number;
  health_score: number;
  overall_assessment: string;
}

interface BenchmarkResult {
  success: boolean;
  industry: string;
  industry_key: string;
  totals?: {
    revenue: number;
    cogs: number;
    operating_expenses: number;
    operating_income: number;
  };
  comparisons: MetricComparison[];
  summary?: BenchmarkSummary;
  forensic_implications: string[];
  error?: string;
}

interface PLBenchmarkComparisonProps {
  reportId: string;
  businessType?: string;
}

export default function PLBenchmarkComparison({ reportId, businessType }: PLBenchmarkComparisonProps) {
  const [result, setResult] = useState<BenchmarkResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadBenchmark();
  }, [reportId]);

  const loadBenchmark = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try cached first
      try {
        const cachedResponse = await api.get<BenchmarkResult & { success?: boolean; cached?: boolean }>(
          `/api/v1/pl-benchmark/cached/${reportId}`
        );
        if (cachedResponse?.success && cachedResponse?.cached) {
          setResult(cachedResponse);
          setLoading(false);
          return;
        }
      } catch (e) {
        // No cache, continue to fetch
      }

      // Fetch fresh comparison
      const response = await api.get<BenchmarkResult>(`/api/v1/pl-benchmark/compare/${reportId}`);
      setResult(response);
    } catch (err: any) {
      setError(err?.message || err?.response?.data?.detail || 'Failed to load benchmark comparison');
    } finally {
      setLoading(false);
    }
  };

  const getDeviationColor = (level: string): { bg: string; text: string; border: string } => {
    switch (level) {
      case 'favorable': return { bg: 'transparent', text: 'var(--color-verified)', border: 'var(--color-verified)' };
      case 'normal': return { bg: 'var(--color-needs-human-tint)', text: 'var(--color-needs-human-text)', border: 'var(--color-needs-human-tint-bd)' };
      case 'moderate': return { bg: 'var(--color-needs-human-tint)', text: 'var(--color-needs-human-text)', border: 'var(--color-needs-human-tint-bd)' };
      case 'high': return { bg: 'var(--color-needs-human-tint)', text: 'var(--color-needs-human-text)', border: 'var(--color-needs-human-tint-bd)' };
      case 'critical': return { bg: 'var(--color-needs-human-tint)', text: 'var(--color-needs-human-text)', border: 'var(--color-needs-human-tint-bd)' };
      default: return { bg: 'var(--color-bg-elevated)', text: 'var(--color-text-primary)', border: 'var(--color-border)' };
    }
  };

  const getHealthScoreColor = (score: number): string => {
    if (score >= 80) return 'var(--color-verified)';
    if (score >= 60) return 'var(--color-needs-human-text)';
    if (score >= 40) return 'var(--color-needs-human-text)';
    return 'var(--color-needs-human-text)';
  };

  const getHealthScoreBg = (score: number): string => {
    if (score >= 80) return 'var(--chart-positive)';
    if (score >= 60) return 'var(--chart-1)';
    if (score >= 40) return 'var(--chart-3)';
    return 'var(--chart-negative)';
  };

  if (loading) {
    return (
      <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
          <span className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Loading benchmark comparison...</span>
        </div>
      </div>
    );
  }

  if (error || !result?.success) {
    return (
      <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="w-6 h-6" style={{ color: 'var(--color-text-dim)' }} />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Industry Benchmark Comparison</h3>
        </div>
        <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <div className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
            <Info className="w-5 h-5" />
            <span>{result?.error || error || 'No P&L data available for benchmark comparison'}</span>
          </div>
        </div>
      </div>
    );
  }

  const { summary, comparisons, forensic_implications, industry, totals } = result;

  return (
    <div className="rounded-xl border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Industry Benchmark Comparison</h3>
              <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                <Building2 className="w-4 h-4" />
                <span>{industry}</span>
              </div>
            </div>
          </div>
          <button
            onClick={loadBenchmark}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--color-text-dim)' }}
            title="Refresh comparison"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--color-border-sub)' }}>
          <div className="grid grid-cols-4 gap-4">
            {/* Health Score */}
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    stroke="var(--chart-track)"
                    strokeWidth="6"
                    fill="none"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    stroke={getHealthScoreBg(summary.health_score)}
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={`${summary.health_score * 2.2} 220`}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute text-xl font-bold" style={{ color: getHealthScoreColor(summary.health_score) }}>
                  {summary.health_score}
                </span>
              </div>
              <p className="text-sm font-medium mt-1" style={{ color: 'var(--color-text-secondary)' }}>Health Score</p>
            </div>

            {/* Favorable */}
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: 'var(--color-verified)' }}>{summary.favorable_count}</div>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Favorable</p>
            </div>

            {/* Normal */}
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{summary.normal_count}</div>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Normal</p>
            </div>

            {/* High Deviation */}
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{summary.high_deviation_count}</div>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>High Deviation</p>
            </div>
          </div>

          <p className="mt-4 text-sm text-center" style={{ color: 'var(--color-text-secondary)' }}>
            {summary.overall_assessment}
          </p>
        </div>
      )}

      {/* Forensic Implications */}
      {forensic_implications.length > 0 && (
        <div className="px-6 py-3 border-b" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Forensic Implications:</p>
              <ul className="mt-1 text-sm list-disc list-inside" style={{ color: 'var(--color-needs-human-text)' }}>
                {forensic_implications.map((imp, idx) => (
                  <li key={idx}>{imp}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Metric Comparisons */}
      <div className="px-6 py-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between py-2 text-sm font-medium"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          <span>Detailed Metric Comparison ({comparisons.length} metrics)</span>
          <span style={{ color: 'var(--color-needs-human-text)' }}>{expanded ? 'Hide' : 'Show'} Details</span>
        </button>

        {expanded && (
          <div className="mt-4 space-y-3">
            {comparisons.map((metric) => {
              const colors = getDeviationColor(metric.deviation_level);
              
              return (
                <div
                  key={metric.metric_key}
                  className="border rounded-lg p-4"
                  style={{ borderColor: colors.border }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{metric.metric_name}</h4>
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{metric.description}</p>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: colors.bg, color: colors.text }}>
                      {metric.deviation_level.charAt(0).toUpperCase() + metric.deviation_level.slice(1)}
                    </span>
                  </div>

                  {/* Visual Bar */}
                  <div className="relative h-6 rounded-full overflow-hidden mt-3" style={{ backgroundColor: 'var(--chart-track)' }}>
                    {/* Benchmark Range */}
                    <div
                      className="absolute h-full"
                      style={{
                        backgroundColor: 'var(--chart-grid)',
                        left: `${Math.max(0, (metric.benchmark_low / (metric.benchmark_high * 1.5)) * 100)}%`,
                        width: `${((metric.benchmark_high - metric.benchmark_low) / (metric.benchmark_high * 1.5)) * 100}%`
                      }}
                    />
                    {/* Benchmark Mid Line */}
                    <div
                      className="absolute h-full w-0.5"
                      style={{
                        backgroundColor: 'var(--chart-axis)',
                        left: `${(metric.benchmark_mid / (metric.benchmark_high * 1.5)) * 100}%`
                      }}
                    />
                    {/* Business Value Marker */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 shadow"
                      style={{
                        borderColor: 'var(--color-bg-surface)',
                        backgroundColor:
                          metric.deviation_level === 'favorable' ? 'var(--chart-positive)' :
                          metric.deviation_level === 'normal' ? 'var(--chart-1)' :
                          metric.deviation_level === 'moderate' ? 'var(--chart-3)' :
                          'var(--chart-negative)',
                        left: `${Math.min(95, Math.max(5, (metric.business_value / (metric.benchmark_high * 1.5)) * 100))}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                    />
                  </div>

                  {/* Values */}
                  <div className="flex justify-between mt-2 text-xs">
                    <span style={{ color: 'var(--color-text-muted)' }}>
                      Industry: {metric.benchmark_low}{metric.unit} - {metric.benchmark_high}{metric.unit}
                    </span>
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      Your Value: {metric.business_value}{metric.unit}
                    </span>
                  </div>

                  {/* Explanation */}
                  <p className="mt-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{metric.explanation}</p>

                  {/* Claim Impact */}
                  {metric.claim_impact && (
                    <p className="mt-1 text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
                      <Target className="inline w-3 h-3 mr-1" />
                      {metric.claim_impact}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Financial Totals */}
      {totals && totals.revenue > 0 && (
        <div className="px-6 py-4 border-t rounded-b-xl" style={{ borderColor: 'var(--color-border-sub)', backgroundColor: 'var(--color-bg-elevated)' }}>
          <div className="grid grid-cols-4 gap-4 text-center text-sm">
            <div>
              <p style={{ color: 'var(--color-text-muted)' }}>Revenue</p>
              <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={totals.revenue} /></p>
            </div>
            <div>
              <p style={{ color: 'var(--color-text-muted)' }}>COGS</p>
              <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={totals.cogs} /></p>
            </div>
            <div>
              <p style={{ color: 'var(--color-text-muted)' }}>Op. Expenses</p>
              <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={totals.operating_expenses} /></p>
            </div>
            <div>
              <p style={{ color: 'var(--color-text-muted)' }}>Op. Income</p>
              <p className="font-semibold" style={{ color: totals.operating_income >= 0 ? 'var(--color-verified)' : 'var(--color-text-muted)' }}>
                <FinancialValue value={totals.operating_income} />
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
